import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { Voordracht } from '../../api/typen.ts';
import { useVoordrachtMutaties } from '../../api/voordrachten.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import { Keuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { naarGetal, vandaag } from '../../helpers/formulier.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

type Uitkomst = 'geplaatst' | 'afgewezen' | 'teruggetrokken';

interface Waarden {
  uitkomst: Uitkomst;
  startdatumPlaatsing: string;
  tarief: string;
  notitie: string;
}

const uitkomstOpties = [
  { waarde: 'geplaatst', label: 'Geplaatst' },
  { waarde: 'afgewezen', label: 'Afgewezen' },
  { waarde: 'teruggetrokken', label: 'Teruggetrokken' },
] as const;

/**
 * Rondt een voordracht af met een plaatsing, afwijzing of terugtrekking. Bij een plaatsing is een startdatum
 * verplicht en krijgt de kandidaat de status geplaatst.
 * @param props De voordracht.
 * @returns De dialoog.
 */
export function AfrondDialoog({
  open,
  onSluit,
  voordracht,
}: {
  open: boolean;
  onSluit: () => void;
  voordracht: Voordracht;
}) {
  const mutaties = useVoordrachtMutaties();
  const formulier = useForm<Waarden>({
    defaultValues: { uitkomst: 'geplaatst', startdatumPlaatsing: vandaag(), tarief: '', notitie: '' },
  });
  useEffect(() => {
    if (open) {
      formulier.reset({ uitkomst: 'geplaatst', startdatumPlaatsing: vandaag(), tarief: '', notitie: '' });
    }
  }, [open, formulier]);
  const uitkomst = formulier.watch('uitkomst');

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    (waarden) => {
      const notitie = waarden.notitie || null;
      if (waarden.uitkomst === 'geplaatst') {
        return mutaties.rondAf.mutateAsync({
          id: voordracht.id,
          invoer: {
            uitkomst: 'geplaatst',
            startdatumPlaatsing: waarden.startdatumPlaatsing,
            tarief: naarGetal(waarden.tarief),
            notitie,
          },
        });
      }
      return mutaties.rondAf.mutateAsync({ id: voordracht.id, invoer: { uitkomst: waarden.uitkomst, notitie } });
    },
    { melding: 'Voordracht afgerond', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel="Voordracht afronden"
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        verzendLabel="Afronden"
        maxWidth="xs"
      >
        <Stack spacing={2}>
          <Keuzeveld naam="uitkomst" label="Uitkomst" opties={uitkomstOpties} verplicht autoFocus />
          {uitkomst === 'geplaatst' && (
            <>
              <Tekstveld naam="startdatumPlaatsing" label="Startdatum plaatsing" type="date" verplicht />
              <Tekstveld naam="tarief" label="Tarief" type="number" stap="0.01" min={0} hulptekst="Optioneel" />
            </>
          )}
          <Tekstveld naam="notitie" label="Notitie" meerregelig rijen={3} />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}
